import {
  KeyboardEvent,
  PointerEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material";

import useLocalStorage from "hooks/useLocalStorage.hook";

import {
  ASSISTANT_PANEL_DEFAULT_SIZE,
  ASSISTANT_PANEL_SIZE_STORAGE_KEY,
} from "../constants";
import {
  IAssistantPanelResizeStart,
  IAssistantPanelSize,
  IAssistantPanelPosition,
  IUseAssistantPanelResizeOptions,
  IUseAssistantPanelResizeReturn,
} from "../types";
import {
  clampAssistantPanelSize,
  getAssistantPanelResizeFrame,
  isAssistantPanelSize,
} from "../utils";

export const useAssistantPanelResize = ({
  panelRef,
  visible,
  onPositionChange,
}: IUseAssistantPanelResizeOptions): IUseAssistantPanelResizeReturn => {
  const theme = useTheme();
  const gap = parseFloat(theme.spacing(4));
  const keyboardStep = parseFloat(theme.spacing(5));
  const [storedSize, setStoredSize] = useLocalStorage<unknown>(
    ASSISTANT_PANEL_SIZE_STORAGE_KEY
  );
  const preferredSize = isAssistantPanelSize(storedSize)
    ? storedSize
    : ASSISTANT_PANEL_DEFAULT_SIZE;
  const [size, setSize] = useState<IAssistantPanelSize>(preferredSize);
  const [position, setPosition] = useState<IAssistantPanelPosition>();
  const [isResizeActive, setIsResizeActive] = useState(false);
  const origin = useRef<IAssistantPanelResizeStart>();

  const handleFinish = useCallback(() => {
    const current = origin.current?.current;
    origin.current = undefined;
    if (current) {
      setStoredSize(current.size);
      onPositionChange(current.position);
    }
    setIsResizeActive(false);
    setPosition(undefined);
  }, [onPositionChange, setStoredSize]);

  useLayoutEffect(() => {
    if (!visible) {
      origin.current = undefined;
      setIsResizeActive(false);
      setPosition(undefined);
      setSize(ASSISTANT_PANEL_DEFAULT_SIZE);
      setStoredSize(null);
      return;
    }
    const handleViewportResize = () => {
      origin.current = undefined;
      setIsResizeActive(false);
      setPosition(undefined);
      setSize(
        clampAssistantPanelSize(
          preferredSize,
          { width: window.innerWidth, height: window.innerHeight },
          gap
        )
      );
    };
    handleViewportResize();
    window.addEventListener("resize", handleViewportResize);
    return () => {
      origin.current = undefined;
      window.removeEventListener("resize", handleViewportResize);
    };
  }, [gap, preferredSize, setStoredSize, visible]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const bounds = panelRef.current?.getBoundingClientRect();
      if (!bounds || !event.isPrimary || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      origin.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        bounds,
      };
      setPosition({ left: bounds.left, top: bounds.top });
      setIsResizeActive(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [panelRef]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const start = origin.current;
      if (!start || event.pointerId !== start.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const frame = getAssistantPanelResizeFrame(
        start.bounds,
        {
          width: start.bounds.width + start.clientX - event.clientX,
          height: start.bounds.height + start.clientY - event.clientY,
        },
        gap
      );
      start.current = frame;
      setSize(frame.size);
      setPosition(frame.position);
    },
    [gap]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const bounds = panelRef.current?.getBoundingClientRect();
      if (!bounds) return;
      let width = bounds.width;
      let height = bounds.height;
      switch (event.key) {
        case "ArrowLeft":
          width += keyboardStep;
          break;
        case "ArrowRight":
          width -= keyboardStep;
          break;
        case "ArrowUp":
          height += keyboardStep;
          break;
        case "ArrowDown":
          height -= keyboardStep;
          break;
        default:
          return;
      }
      event.preventDefault();
      event.stopPropagation();
      const frame = getAssistantPanelResizeFrame(
        bounds,
        { width, height },
        gap
      );
      setSize(frame.size);
      setStoredSize(frame.size);
      onPositionChange(frame.position);
    },
    [gap, keyboardStep, onPositionChange, panelRef, setStoredSize]
  );

  return {
    size,
    position,
    isResizeActive,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handleFinish,
      onPointerCancel: handleFinish,
      onLostPointerCapture: handleFinish,
      onKeyDown: handleKeyDown,
    },
  };
};
